import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TopicIcon from '@mui/icons-material/Topic';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AppColors } from '../theme/colors';
import { getBoxTemplate, type IndustryType } from '../constants/industryTemplates';

interface WelcomeModalProps {
  open?: boolean;
  onClose?: () => void;
  industryType?: IndustryType | string;
}

type PostType = 'voice' | 'thanks' | 'theme';

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ open: controlledOpen, onClose, industryType }) => {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    // controlledOpenが指定されていない場合は、毎回表示する（ブラウザに状態を保存しない）
    if (controlledOpen === undefined) {
      setInternalOpen(true);
    }
  }, [controlledOpen]);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleClose = () => {
    setInternalOpen(false);
    onClose?.();
  };

  const handleStartPost = (type: PostType) => {
    // 先にモーダルを閉じてから遷移する（戻った時にモーダルが残らないようにする意図）
    handleClose();
    navigate(`/post/new?type=${type}`);
  };

  const features = [
    {
      type: 'voice' as const,
      icon: CampaignIcon,
      color: AppColors.voiceBoxColor,
      title: getBoxTemplate(industryType, 'voice').name,
      description: getBoxTemplate(industryType, 'voice').shortDescription,
    },
    {
      type: 'thanks' as const,
      icon: FavoriteIcon,
      color: AppColors.thanksBoxColor,
      title: getBoxTemplate(industryType, 'thanks').name,
      description: getBoxTemplate(industryType, 'thanks').shortDescription,
    },
    {
      type: 'theme' as const,
      icon: TopicIcon,
      color: AppColors.themeBoxColor,
      title: getBoxTemplate(industryType, 'theme').name,
      description: getBoxTemplate(industryType, 'theme').shortDescription,
    },
  ];

  const checkItems = [
    '完全匿名で安心して投稿できます',
    '3種類のボックスで用途に合わせて投稿',
    'あなたの声が会社の改善につながります',
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
          py: 4,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            fontWeight: 700,
            mb: 1,
          }}
        >
          LiaVoiceへようこそ！
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: 14,
          }}
        >
          あなたの声で職場が変わる
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* 説明文 */}
        <Typography
          sx={{
            textAlign: 'center',
            color: AppColors.textSecondary,
            fontSize: 14,
            mb: 3,
            lineHeight: 1.8,
          }}
        >
          LiaVoiceは、従業員の声を匿名で集め、<br />
          より良い職場環境づくりを支援する<br />
          コミュニケーションツールです。
        </Typography>

        {/* 3つのボックス */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            justifyContent: 'center',
          }}
        >
          {features.map((feature) => {
            const IconComponent = feature.icon;
            return (
              <Box
                key={feature.title}
                role="button"
                tabIndex={0}
                onClick={() => handleStartPost(feature.type)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStartPost(feature.type);
                  }
                }}
                sx={{
                  flex: 1,
                  maxWidth: 120,
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 3,
                  bgcolor: `${feature.color}15`,
                  border: `1px solid ${feature.color}40`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    borderColor: `${feature.color}80`,
                    boxShadow: `0 6px 18px ${feature.color}25`,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${AppColors.primary}55`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: `${feature.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <IconComponent sx={{ color: feature.color, fontSize: 24 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: AppColors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    color: AppColors.textSecondary,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* チェックリスト */}
        <Box sx={{ mb: 3 }}>
          {checkItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{ color: AppColors.success, fontSize: 20 }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  color: AppColors.textPrimary,
                }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 閉じるボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              px: 4,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            閉じる
          </Button>
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
